import { sumBy } from 'lodash';
import { FeeUnits, StorageFeeUnits } from '~/constants';
import getBudgetCostAmount from './getBudgetCostAmount';
import { Project } from '~/types/project';
import { getExpectedItcCost } from './costsFns';
import { System } from '.';

export const getBuyerFeeData = (project: Project) => {
  if (!project) {
    return null;
  }

  let buyerData: any; //TODO typing
  if (project?.activeInvestorQueueRow) {
    buyerData = project?.activeInvestorQueueRow;
  } else if (project?.investorQueue) {
    buyerData = project?.investorQueue?.[0];
  } else {
    return null;
  }

  const buyerFeeData = buyerData && Object.keys(buyerData)
    .reduce((acc: any, key: any) => {
      if (key.toLowerCase().includes('fee') || key.toLowerCase().includes('unit')) {
        acc[key] = buyerData[key];
      }
      return acc;
    }, {});

  return {
    nrsFee: buyerFeeData?.nrsFee,
    nrsFeeUnit: buyerFeeData?.nrsFeeUnit,
    buyerStorageFee: buyerFeeData?.buyerStorageFee,
    buyerStorageFeeUnit: buyerFeeData?.buyerStorageFeeUnit
  };
};

export const calculateFee = (
  project: Project,
  epcCost: number,
  fees: { fee: number, feeUnit: string } = { fee: 0, feeUnit: '' } //TODO typing
) => {
  const { fee, feeUnit } = fees;
  if (fee === undefined) {
    return null;
  }

  if (feeUnit === FeeUnits.DOLLARS_PER_WDC) {
    const sizeKwdc = project?.projectArrays
      ? sumBy(Object.values(project.projectArrays), (mount: any) => Number(mount.sizeKwdc))
      : 0;

    const perWdcFee = fee * sizeKwdc * 1000;
    //fee dollars per WDC
    return perWdcFee;
  }

  if (feeUnit === FeeUnits.PERCENT_OF_BUDGET && epcCost) {
    if (isNaN(epcCost)) {
      return null;
    }
    //fee percent of budget
    const percentBudgetFee = ((epcCost + getBudgetCostAmount(project, 'OTHER_ITC') + getBudgetCostAmount(project, 'INTERCONNECTION')) / 100) * fee;
    return percentBudgetFee;
  }

  if (feeUnit === FeeUnits.PERCENT_OF_ITC) {
    // pass epcCost from projectCosts
    // this is needed to calculate ITC percentage fee on the sell side
    const itcCostFee = calculateItcFee(project, fee, epcCost);
    //fee percent of ITC
    return itcCostFee;

  } else {
    //flat fee
    return fee;
  }
};

const calculateItcFee = (project: Project, fee: number, epcCost: number) => {
  // pass epcCost from projectCosts
  // this is needed to calculate ITC percentage fee on the sell side
  const itcCost = getExpectedItcCost(project, epcCost);
  const itcCostFee = fee * itcCost / 100;
  return itcCostFee;
};

export const calculateStorageFee = (
  project: Project,
  fees: { storageFee: number, storageFeeUnit: string } = { storageFee: 0, storageFeeUnit: '' }
) => {
  const { storageFee, storageFeeUnit } = fees;

  if (storageFee === undefined) {
    return null;
  }

  //get sum of storage capacity for project
  const projectStorageCapacity = project?.storage
    ?.reduce((acc: any, storage: any) => acc + storage.discharge, 0);

  if (storageFeeUnit === StorageFeeUnits.DOLLARS_PER_KWH) {
    //storage fee per kWh
    const perKwhFee = projectStorageCapacity * storageFee;
    return perKwhFee;
  } else if (storageFeeUnit === StorageFeeUnits.FLAT_FEE) {
    //storage flat fee
    return storageFee;
  }
};

export const calculateStorageFeePerWdc = (
  project: Project,
  feeTotal: number
) => {

  const systemSize = System.sizeKwdc(project);

  if (systemSize === 0) return 0;
  const totalDollarsPerWdc = feeTotal / systemSize / 1000;

  return parseFloat(totalDollarsPerWdc.toFixed(3));

};

export const getFees = (
  project: Project,
  epcCost?: number,
  options: { bid?: any, hasNrsFee?: boolean, hasSellerFee?: boolean } = {} //TODO typing
) => {
  const { bid, hasNrsFee = false, hasSellerFee = false } = options;
  let fee: number = 0;
  let feeUnit: string = '';
  if (hasNrsFee) {
    const buyerFees = getBuyerFeeData(project);
    fee = buyerFees?.nrsFee;
    feeUnit = buyerFees?.nrsFeeUnit;
    const buyerFee = calculateFee(project, epcCost as number, { fee, feeUnit });
    return buyerFee;
  } else if (hasSellerFee) {
    const sellerFees = bid?.sellerFees as any; //TODO typing
    fee = sellerFees?.sellerFee;
    feeUnit = sellerFees?.sellerFeeUnit;
    const sellerFee = calculateFee(project, epcCost as number, { fee, feeUnit });
    return sellerFee;
  }
};

export const getStorageFees = (
  project: Project,
  options: { bid?: any, hasBuyerStorageFee?: boolean, hasSellerStorageFee?: boolean } = {} //TODO typing
) => {
  const { bid, hasBuyerStorageFee = false, hasSellerStorageFee = false } = options;
  let storageFee: number = 0;
  let storageFeeUnit: string = '';

  if (hasBuyerStorageFee) {
    const buyerFees = getBuyerFeeData(project);
    storageFee = buyerFees?.buyerStorageFee;
    storageFeeUnit = buyerFees?.buyerStorageFeeUnit;
    const buyerStorageFee = calculateStorageFee(project, { storageFee, storageFeeUnit });
    return buyerStorageFee;
  } else if (bid && hasSellerStorageFee) {
    const sellerFees = bid?.sellerFees as any; //TODO typing
    storageFee = sellerFees?.sellerStorageFee;
    storageFeeUnit = sellerFees?.sellerStorageFeeUnit;
    const sellerStorageFee = calculateStorageFee(project, { storageFee, storageFeeUnit });
    return sellerStorageFee;
  };
};



